<!--
File: TreatmentMatrixPivot.vue
Description: shows the Treatment Matrix as the pivot table.
-->
<template>
  <base-data-table v-if="eligible" :loading="showSpinner" :showPagination="false">

    <template #header-buttons>
      <md-button v-if="isBtnAllowed('PrintButton')" class="md-success" @click="print" :disabled="showSpinner">
        {{ $t('buttons.print') }}
      </md-button>
      <!--md-button v-if="isBtnAllowed('ExportButton')" class="md-raised md-success" @click="exportToExcel"
        :disabled="showSpinner">
        {{ $t('buttons.excel_export') }}
      </md-button-->
    </template>

    <template #table-rows>
      <span id="matrix" v-html="tableData"></span>
    </template>
  </base-data-table>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BaseDataTable, TreatmentsDropdown } from '@/pages/Components/'
import BaseTableMixin from '@/mixins/BaseTableMixin'
import printReport from '@/mixins/printMixin'

export default {
  name: 'TreatmentMatrixPivot',
  mixins: [printReport, BaseTableMixin],

  data() {
    return {
      formName: 'TreatmentMatrixPivot',
      eligible: false,

      historyMapping: {
        pagination: {},
        searchQuery: ''
      }
    }
  },

  components: {
    BaseDataTable,
    TreatmentsDropdown,
  },

  async mounted() {
    // Ensure treatment matrix list is loaded for color mapping (this contains the color data)
    await this.ensureTreatmentMatrixListLoaded();
  },

  methods: {
    ...mapActions({
      clearList: 'ReferenceData/CLEAR_TREATMENT_MATRIX_LIST',  // Called from the BaseTableMixin's mount()
      loadList: 'ReferenceData/LOAD_TREATMENT_MATRIX_PIVOT_HTML',
      loadTreatmentMatrixList: 'ReferenceData/LOAD_TREATMENT_MATRIX_LIST'
    }),

    print() {
      this.showSpinner = true
      // Wait a bit to ensure DOM is updated with colors
      this.$nextTick(() => {
        this.printReport(this.getPrintHeader)
        this.showSpinner = false
      })
    },

    async exportToExcel() {
      this.$store.dispatch('ReferenceData/LOAD_TREATMENT_MATRIX_PIVOT_EXCEL').then(res => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res]))
        link.download = "treatment_matrix.xlsx"
        link.click()
        URL.revokeObjectURL(link.href)
      })
    },

    async ensureTreatmentMatrixListLoaded() {
      // Load treatment matrix list for color mapping if not already loaded
      if (!this.treatmentMatrixList || this.treatmentMatrixList.length === 0) {
        try {
          await this.loadTreatmentMatrixList({ forDropdown: false });
        } catch (error) {
          console.warn('Failed to load treatment matrix list for color mapping:', error);
        }
      }
    },

    applyTreatmentColors(html) {
      // Ensure html is a string
      if (typeof html !== 'string') {
        console.warn('applyTreatmentColors received non-string input:', html);
        return String(html || '');
      }

      // Use treatment matrix list which already contains color information from the API
      if (this.treatmentMatrixList && Array.isArray(this.treatmentMatrixList)) {
        const treatmentColorMap = {};
        
        // Build color map from treatment matrix API data 
        this.treatmentMatrixList.forEach(treatment => {
          if (treatment.key && treatment.color) {
            treatmentColorMap[treatment.key] = treatment.color;
          }
        });

        // Apply colors based on database color configuration
        Object.keys(treatmentColorMap).forEach(treatmentCode => {
          const dbColor = treatmentColorMap[treatmentCode];
          
          // Convert database color names to hex values (comprehensive list for future API colors)
          const colorMap = {
            // Primary colors
            'red': '#F44336',
            'pink': '#E91E63',
            'purple': '#9C27B0',
            'violet': '#9C27B0',
            'indigo': '#3F51B5',
            'blue': '#2196F3',
            'cyan': '#00BCD4',
            'teal': '#009688',
            'green': '#4CAF50',
            'lime': '#CDDC39',
            'yellow': '#FFD700',
            'amber': '#FFC107',
            'orange': '#FF9800',
            'brown': '#795548',
            
            // Grayscale
            'black': '#000000',
            'grey': '#9E9E9E',
            'gray': '#9E9E9E',
            'white': '#FFFFFF',
            
            // Light variants
            'lightred': '#FFCDD2',
            'lightpink': '#F8BBD9',
            'lightpurple': '#E1BEE7',
            'lightblue': '#BBDEFB',
            'lightcyan': '#B2EBF2',
            'lightgreen': '#C8E6C9',
            'lightyellow': '#FFF9C4',
            'lightorange': '#FFE0B2',
            'lightgray': '#F5F5F5',
            'lightgrey': '#F5F5F5',
            
            // Dark variants
            'darkred': '#C62828',
            'darkpink': '#AD1457',
            'darkpurple': '#6A1B9A',
            'darkblue': '#1565C0',
            'darkcyan': '#00838F',
            'darkgreen': '#2E7D32',
            'darkyellow': '#F57F17',
            'darkorange': '#E65100',
            'darkgray': '#424242',
            'darkgrey': '#424242',
            
            // Additional common colors
            'maroon': '#800000',
            'navy': '#000080',
            'olive': '#808000',
            'aqua': '#00FFFF',
            'fuchsia': '#FF00FF',
            'silver': '#C0C0C0',
            'gold': '#FFD700',
            'coral': '#FF7F50',
            'salmon': '#FA8072',
            'crimson': '#DC143C',
            'magenta': '#FF00FF',
            'turquoise': '#40E0D0',
            'khaki': '#F0E68C',
            'plum': '#DDA0DD',
            'orchid': '#DA70D6'
          };
          
          // Use database color or fallback to the color value if it's already a hex
          const bgColor = colorMap[dbColor?.toLowerCase()] || dbColor || '#757575';
          
          // Determine text color based on background brightness (light colors get black text)
          const lightColors = ['yellow', 'lime', 'lightyellow', 'lightgreen', 'lightblue', 'lightcyan', 
                              'lightpink', 'lightpurple', 'lightorange', 'lightred', 'lightgray', 'lightgrey', 
                              'white', 'silver', 'khaki', 'amber'];
          const textColor = lightColors.includes(dbColor?.toLowerCase()) ? 'black' : 'white';
          
          // Create regex to match the treatment code in table cells
          // Escape special regex characters and handle Cyrillic characters properly
          const escapedCode = treatmentCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`<td([^>]*)>\\s*${escapedCode}\\s*</td>`, 'g');
          
          html = html.replace(regex, (match, attributes) => {
            return `<td${attributes} style="background-color: ${bgColor} !important; color: ${textColor} !important; font-weight: bold !important; text-align: center !important;">${treatmentCode}</td>`;
          });
        });
      } else {
        console.warn('Treatment matrix list not available for color mapping');
      }

      return html;
    },
  },

  computed: {
    ...mapState({
      pivot: (state) => state.ReferenceData.treatmentMatrixPivot,
      treatmentMatrixList: (state) => state.ReferenceData.treatment_matrix_list,
    }),
    ...mapGetters('ReferenceData', ['treatmentMatrixEditable']),

    tableData() {
      if (!this.pivot) return ''

      const replacements = {
        key: this.$t('label.score'),
        aadt: this.$t('condition.aadt'),
        cracking: this.$t('condition.cracking'),
        potholes: this.$t('label.potholes'),
        rutting: this.$t('condition.rutting'),
        iri: this.$t('condition.iri'),
      };

      // Ensure pivot is a string
      let html = String(this.pivot || '');

      // Apply localized text replacements
      html = html.replace(new RegExp(Object.keys(replacements).join('|'), 'gi'), match => {
        return replacements[match.toLowerCase()];
      });

      // Apply color coding to treatment codes
      html = this.applyTreatmentColors(html);

      return html;
    },

    getPrintHeader() {
      const today = new Date().toLocaleDateString()
      const matrixElement = this.$el.querySelector('#matrix');
      
      if (!matrixElement) {
        return `<h4 style='text-align:center'>${this.$t('reports.reports_title')}</h4>
                <h4 style='text-align:center'>${this.$t('route.treatment_matrix')} ${this.$t('label.as_on')}${today}</h4>`
      }
      
      // Get the innerHTML and remove buttons, but preserve all styling including inline styles
      let prtHtml = matrixElement.innerHTML.replace(/<button[^>]*>.*?<\/button>/g, '');
      
      // Add print-specific styling to preserve colors
      const printStyles = `
        <style>
          @media print {
            * { -webkit-print-color-adjust: exact !important; }
            td[style*="background-color"] { -webkit-print-color-adjust: exact !important; }
            table { border-collapse: collapse !important; width: 100% !important; }
            td, th { border: 1px solid black !important; padding: 4px !important; }
          }
        </style>
      `;

      return `<h4 style='text-align:center'>${this.$t('reports.reports_title')}</h4>
              <h4 style='text-align:center'>${this.$t('route.treatment_matrix')} ${this.$t('label.as_on')}${today}</h4>
              ${printStyles}
              ${prtHtml}`
    },
  },
}
</script>
<style lang="scss">
$table-cell-padding: .5rem; // default in bs5
$table-cell-padding-sm: .25rem; // default in bs5

.md-card {
  margin: 0px 0;
}

.text_rotate {
  rotate: -90deg;
  white-space: pre;
  width: 50px;
  text-align: center;
  padding: 0px;
}

/* Treatment Matrix Color Coding - Colors applied directly via JavaScript */
/* Ensure proper spacing in table cells */
#matrix td {
  text-align: center !important;
  padding: 8px !important;
}

#matrix table {
  border: 0.5px solid;
  border-collapse: collapse;
}

#matrix td,
#matrix th {
  border: 0.5px solid;
}

/* Print styles for treatment matrix */
@media print {
  /* Ensure colors are preserved in print */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Force cell colors to print */
  #matrix td[style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Ensure proper text visibility in print */
  #matrix td {
    color: black !important;
  }
  
  /* Override text color for colored cells to maintain readability */
  #matrix td[style*="background-color"][style*="color: white"] {
    color: white !important;
  }
  
  #matrix td[style*="background-color"][style*="color: black"] {
    color: black !important;
  }
  
  /* Ensure table structure is preserved */
  #matrix table {
    border-collapse: collapse !important;
    width: 100% !important;
  }
  
  #matrix td,
  #matrix th {
    border: 1px solid black !important;
    padding: 4px !important;
    font-size: 12px !important;
  }
  
  /* Fix any text that might not be black */
  #matrix th {
    color: black !important;
    font-weight: bold !important;
  }
}
</style>